import { Button, MenuItem, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface DeletePlaylistMenuItemProps {
    deleteUrl: string;
    confirmationTitle: string;
    confirmationMessage: string;
    confirmationButtonText: string;
    playlistName: string;
    playlistId: string;
    onCloseMenu: () => void;
    onReload?: () => void;
}

/**
 * playlist menu item for deleting the playlist.
 */
export const DeletePlaylistMenuItem = (props: DeletePlaylistMenuItemProps) => {
    const {
        deleteUrl,
        confirmationTitle,
        confirmationMessage,
        confirmationButtonText,
        playlistName,
        playlistId,
        onCloseMenu,
        onReload,
    } = props;
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const closeConfirmation = () => {
        setOpenConfirmation(false);
        onCloseMenu();
    };

    const { showToast } = useToastsContext();

    const sendButtonAnalytics = useButtonAnalytics();

    const handleDelete = async () => {
        setProcessing(true);
        sendButtonAnalytics(
            "Confirm Delete",
            ButtonClickAnalyticsType.DELETE,
            playlistId,
            "my-playlists",
            "Modal - Delete playlist"
        );
        try {
            const result = await postKmsData(deleteUrl);
            if (result.success) {
                showToast({
                    severity: "success",
                    message: translate('Playlist "%1" successfully deleted.', [playlistName]),
                });
                onReload?.();
            }
            else {
                showToast({
                    severity: "error",
                    message: translate('Failed to delete the playlist "%1"', [playlistName]),
                });
            }
            setProcessing(false);
            closeConfirmation();
        }
        catch (e: unknown) {
            closeConfirmation();
            setProcessing(false);
            showToast({ severity: "error", message: translate('Failed to delete the playlist "%1"', [playlistName]) });
        }
    };

    const onMenuButtonClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics("Delete Playlist", ButtonClickAnalyticsType.DELETE, playlistId);
    };

    return (
        <>
            <MenuItem onClick={onMenuButtonClick}>{translate("Delete")}</MenuItem>

            <Modal
                open={openConfirmation}
                onClose={closeConfirmation}
                maxWidth={false}
                onKeyDown={(ev) => ev.stopPropagation()}
            >
                <ModalTitle>{confirmationTitle}</ModalTitle>
                <ModalContent>{HtmlReactParser(confirmationMessage)}</ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={closeConfirmation}>
                        {translate("Cancel")}
                    </Button>
                    <Button color="danger" onClick={handleDelete} loading={processing} disabled={processing}>
                        {confirmationButtonText}
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
};
